import {LinkButton} from '@aboutyou/router';
import styled, {
  css,
  FlattenInterpolation,
  ThemedStyledProps,
} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {Z_INDEXES} from '../../../constants/layers';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {UnstyledLink} from '../../atoms/UnstyledLink/UnstyledLink';
import {BadgeListSmall} from '../BadgeList/BadgeList';

type LevelSwitch<T> = {
  [key: number]: T | undefined;
  default: T;
};

const commonLinkStyles = css<{
  $active: boolean;
  $current: boolean;
  $level: number;
  $backgroundColor?: string;
  $showChildrenInBold?: boolean;
  $disabled?: boolean;
}>`
  align-items: center;
  position: relative;
  display: flex;
  cursor: pointer;
  list-style: none;
  box-sizing: border-box;
  font-weight: ${({$active}) =>
    $active ? FONT_WEIGHTS.bold : FONT_WEIGHTS.light};

  ${({$backgroundColor}) =>
    $backgroundColor &&
    css`
      width: fit-content;

      &:before {
        content: '';
        position: absolute;
        left: -6px;
        top: 3px;
        z-index: ${Z_INDEXES.CATEGORY_TREE_BACKGROUND};

        width: calc(100% + 12px);
        height: calc(100% - 4px);

        border-radius: 4px;
        background-color: ${$backgroundColor};
      }
    `}

  ${({$level}) =>
    levelSpecificLinkStyles[$level] || levelSpecificLinkStyles.default};

  ${({$disabled}) =>
    $disabled &&
    css`
      cursor: default;
    `}
`;

export const StyledLink = styled(UnstyledLink)`
  ${commonLinkStyles}
`;

export const StyledLinkButton = styled(LinkButton)`
  ${commonLinkStyles}
`;

export const StyledText = styled.span<{
  $textColor?: string;
  $disabled: boolean;
}>`
  transition: opacity 100ms ease-in-out;
  z-index: ${Z_INDEXES.ONE};
  color: ${({$textColor, $disabled}) => {
    if ($textColor) {
      return $textColor;
    }
    return $disabled ? COLORS.Grey2 : COLORS.Black;
  }};
`;

const levelSpecificLinkStyles: LevelSwitch<
  FlattenInterpolation<
    ThemedStyledProps<
      {
        active?: boolean;
        $showChildrenInBold?: boolean;
      },
      unknown
    >
  >
> = {
  0: css<{$active?: boolean}>`
    font-size: ${FONT_SIZES.px18};
    padding: 6px 0;
    line-height: ${FONT_SIZES.px24};
    font-weight: ${({$active}) =>
      $active ? FONT_WEIGHTS.bold : FONT_WEIGHTS.mediumBold};
  `,
  1: css`
    font-size: ${FONT_SIZES.px14};
    line-height: ${FONT_SIZES.px20};
    padding: 4px 0;
  `,
  default: css<{
    $showChildrenInBold?: boolean;
  }>`
    font-size: ${FONT_SIZES.px12};
    line-height: ${FONT_SIZES.px18};
    padding: 4px 0;
    ${({$showChildrenInBold}) =>
      $showChildrenInBold && `font-weight: ${FONT_WEIGHTS.bold};`}
  `,
};

export const ChildrenWrapper = styled.ul<{
  $level: number;
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 0;
  padding: 4px 0;

  ${({$level}) => css`
    padding-left: ${levelIndentation[$level] || levelIndentation.default};
  `};
`;

const levelIndentation: LevelSwitch<string> = {
  0: '5%',
  1: '8%',
  default: '8%',
};

export const BadgeList = styled(BadgeListSmall)`
  display: inline-block;
  vertical-align: text-top;
  margin-left: 0.5rem;
`;
